import React, { useEffect, useState } from "react";
import { Button, Form, Input, message, Select, Table } from "antd";
import { useSelector } from "react-redux";
import { fetchStates } from "../state/FetchStates";
import { fetchCities } from "./FetchCities";
import { EditOutlined } from "@ant-design/icons";
import { selectBaseUrl, selectProdTestUrl } from "../../redux/slices/envSlice";
import { create_city, update_city } from "../../endpoints/API";

const City = () => {
  const baseUrl = useSelector(selectBaseUrl);
  const prodTestUrl = useSelector(selectProdTestUrl);
  const isUser = useSelector((state) => state.user.user); // Fetch user data from Redux
  const [states, setStates] = useState([]); // State to store states data
  const [cities, setCities] = useState([]); // State to store cities data
  const [form] = Form.useForm(); // Initialize form instance
  const [editingCity, setEditingCity] = useState(null); // State to store the city being edited

  useEffect(() => {
    const getStatesAndCities = async () => {
      try {
        await fetchStates(setStates, baseUrl, prodTestUrl);
        await fetchCities(setCities, baseUrl, prodTestUrl);
      } catch (error) {
        message.error(error?.response?.data?.error||"Failed to fetch states or cities.")
      }
    };

    getStatesAndCities();
  }, []);

  const onFinish = async (values) => {
    try {
      const payload = {
        created_by: isUser.user_id,
        name: values.city.trim(),
        state: values.state,
        id:editingCity?.id,
      };

      const response = editingCity
        ? await update_city(payload)
        : await create_city(payload);
      if (response.statusText=="OK") {
        const newCity = response?.data;
        message.success(
          editingCity
            ? "City updated successfully!"
            : "City added successfully!"
        );

        const associatedState = states.find(
          (state) => state.id === values.state
        );
        if (associatedState) {
          newCity.state = associatedState; // Attach the full state object to the new city
        }

        // Update cities state to reflect the new city in the table
        if (editingCity) {
          setCities((prevCities) =>
            prevCities.map((city) => (city.id === newCity.id ? newCity : city))
          );
        } else {
          setCities((prevCities) => [...prevCities, newCity]);
        }

        // Reset form fields
        form.resetFields();
        setEditingCity(null); // Clear editing city state
      } else {
        message.error("City already exists.");
      }
    } catch (error) {
      message.error(error?.response?.data?.error||"An error occurred while adding or updating the city.");
    }
  };

  const handleEdit = (city) => {
    setEditingCity(city); // Set the city being edited
    form.setFieldsValue({
      city: city.name,
      state: city.state.id,
    });
  };

  const columns = [
    {
      title: "City",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
      render: (state) => state?.name,
    },
    {
      title: "Edit",
      dataIndex: "edit",
      key: "edit",
      render: (_, city) => (
        <Button onClick={() => handleEdit(city)}>
          <EditOutlined />
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div>
        <Form form={form} onFinish={onFinish}>
          <div
            className="border flex gap-5 p-4 items-center rounded-lg shadow-md"
            style={{ padding: "16px" }}
          >
            <Form.Item
              label={<span className="font-semibold">Add City</span>}
              name="city"
              rules={[{ required: true, message: "Please enter city!" }]}
              style={{ margin: "0" }}
            >
              <Input placeholder="Enter City" style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item
              label={<span className="font-semibold">Select State</span>}
              name="state"
              rules={[{ required: true, message: "Please select a state!" }]}
              style={{ margin: "0" }}
            >
              <Select placeholder="Select state" style={{ width: "100%" }}>
                {states?.map((state) => (
                  <Select.Option key={state.id} value={state.id}>
                    {state.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item style={{ margin: "0" }}>
              <Button type="primary" htmlType="submit">
                {editingCity ? "Update" : "Save"}
              </Button>
            </Form.Item>
          </div>
        </Form>

        <Table dataSource={cities} columns={columns} rowKey="id" />
      </div>
    </div>
  );
};

export default City;
